import React, { useState } from "react";
import { useSelector } from "react-redux";
import {
    DataTable, Table, TableBody, TableCell,
    TableContainer, TableHead, TableHeader, TableRow,
    InlineLoading, Pagination,
} from "@carbon/react";
import { useSorting } from "../../../modules/hooks/sorting";

const PAGE_SIZE = 7;

const headerData = [
    { header: "Evento", key: "tipo" },
    { header: "Inicio", key: "inicio" },
    { header: "Termino", key: "fin" },
    { header: "Duración", key: "duracion" },
    { header: "(L) de combustible", key: "litros" },
    { header: "(L) inicial", key: "inicial" },
    { header: "(L) final", key: "final" },
];

const FuelTable = ({ patent }) => {
    const { eventosPorCamion, loading_fueles } = useSelector(state => state.Tanker) ?? {};
    const eventos = eventosPorCamion?.[patent] ?? [];
    const [page, setPage] = useState(1);

    const toSantiago = (isoStr) => {
        if (!isoStr || isoStr === '-') return '-';
        return new Date(isoStr).toLocaleString('es-CL', {
            timeZone: 'America/Santiago',
            day: '2-digit', month: '2-digit', year: 'numeric',
            hour: '2-digit', minute: '2-digit',
        });
    };

    // Ordenar por inicio desc para que la tabla muestre más reciente primero
    // pero calcular (L) inicial y (L) final desde el orden cronológico
    const eventosOrdenados = [...eventos].sort((a, b) => new Date(a.inicio) - new Date(b.inicio));

    const tableRows = [...eventosOrdenados]
        .reverse()
        .map((e, idx, arr) => {
            // remanente_final = combustible_remanente de esta fila
            const remanenteFinal = e.combustible_remanente ?? 0;
            // remanente_inicial = remanente_final +/- volumen según tipo
            const volumen = e.volumen_total ?? 0;
            const remanenteInicial = e.tipo === 'abastecimiento'
                ? remanenteFinal - volumen
                : remanenteFinal + volumen;

            return {
                id: String(idx),
                tipo: e.tipo === 'abastecimiento' ? 'Carga' : 'Despacho',
                inicio: toSantiago(e.inicio),
                fin: toSantiago(e.fin),
                duracion: e.duracion_min != null ? `${e.duracion_min} min` : '0 min',
                litros: volumen != null ? `${volumen.toLocaleString('es-CL')} L` : '-',
                inicial: `${remanenteInicial.toLocaleString('es-CL')} L`,
                final: `${remanenteFinal.toLocaleString('es-CL')} L`,
            };
        });

    const { rows, headers } = useSorting(tableRows, headerData);
    const paginatedRows = rows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

    if (loading_fueles) return <InlineLoading description="Cargando tabla..." />;

    return (
        <DataTable rows={paginatedRows} headers={headers} width="100%">
            {({ rows, headers, getHeaderProps, getRowProps, getTableProps, getTableContainerProps }) => (
                <TableContainer
                    {...getTableContainerProps()}
                    title="Tabla de abastecimientos a equipos"
                    description="Esta tabla muestra despachos detectados y las cargas registradas de forma manual."
                >
                    <Table {...getTableProps()} aria-label="fuel table">
                        <TableHead>
                            <TableRow>
                                {headers.map((header, i) => {
                                    const { key, ...headerProps } = getHeaderProps({ header });
                                    return (
                                        <TableHeader key={key} {...headerProps}>
                                            {header.header}
                                        </TableHeader>
                                    );
                                })}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows.map(row => {
                                const { key, ...rowProps } = getRowProps({ row });
                                return (
                                    <TableRow key={key} {...rowProps}>
                                        {row.cells.map(cell => (
                                            <TableCell key={cell.id}>{cell.value}</TableCell>
                                        ))}
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                    <Pagination
                        totalItems={tableRows.length}
                        pageSizes={[PAGE_SIZE]}
                        pageSize={PAGE_SIZE}
                        page={page}
                        itemsPerPageText="Filas por página"
                        itemRangeText={(min, max) => `${min}–${max} filas`}
                        pageRangeText={(current, total) => `${current} de ${total}`}
                        forwardText="Página siguiente"
                        backwardText="Página anterior"
                        onChange={({ page }) => setPage(page)}
                    />
                </TableContainer>
            )}
        </DataTable>
    );
};

export default FuelTable;